import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import type { ComponentRef, OnDestroy } from '@angular/core';
import { Directive, ElementRef, ViewContainerRef, inject } from '@angular/core';

import type { Subscription } from 'rxjs';

import { ReportTooltipController, ReportTooltipTemplateContext } from '@core/reports-types';

import { reportTooltipPositions } from './report-tooltip-positions';
import { ReportTooltipComponent } from './report-tooltip.component';

@Directive({
  selector: '[rtReportTooltipOutlet]',
  exportAs: 'reportTooltipOutlet',
  standalone: true,
})
export class ReportTooltipOutletDirective implements ReportTooltipController, OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly elRef = inject(ElementRef);
  private readonly scrollDispatcher = inject(ScrollDispatcher, { optional: true });

  private readonly positions = [
    reportTooltipPositions.above,
    reportTooltipPositions.below,
    reportTooltipPositions.left,
    reportTooltipPositions.right,
  ];

  private container: OverlayRef;
  private portal: ComponentPortal<ReportTooltipComponent>;
  private ref: ComponentRef<ReportTooltipComponent> | null;
  private positionStrategy = this.overlay
    .position()
    .flexibleConnectedTo(this.elRef)
    .withPositions(this.positions)
    .withFlexibleDimensions(false)
    .withPush(false);
  private created: boolean = false;
  private scrollSub: Subscription;
  private x: number;
  private y: number;
  private context: ReportTooltipTemplateContext;

  public ngOnDestroy() {
    if (this.created) this.dispose();
  }

  public show() {
    if (!this.context) return;

    if (!this.created) {
      this.create();
    }
  }

  public hide() {
    if (this.created) {
      this.dispose();
    }
  }

  public setContext(context: ReportTooltipTemplateContext) {
    this.updateContext(context);
  }

  public setPosition(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.updatePosition();
  }

  public update() {
    this.ref?.changeDetectorRef.detectChanges();
  }

  public measure(): [width: number, height: number] {
    if (!this.created) return [0, 0];

    const { width, height } = this.container.overlayElement.getBoundingClientRect();
    return [width, height];
  }

  private create() {
    if (this.created) return;

    const config = new OverlayConfig({
      width: 260,
      height: 'auto',
      hasBackdrop: false,
      positionStrategy: this.positionStrategy,
    });
    this.container = this.overlay.create(config);
    this.portal = new ComponentPortal(ReportTooltipComponent, this.vcRef);
    this.ref = this.container.attach(this.portal);
    // disablePointerEvents = true
    this.container.overlayElement.style.pointerEvents = 'none';
    this.scrollSub = this.scrollDispatcher!.scrolled(0).subscribe(() => this.updatePosition());
    this.created = true;
    this.updateContext(this.context);
  }

  private dispose() {
    this.container.dispose();
    this.scrollSub.unsubscribe();
    this.ref = null;
    this.created = false;
  }

  private updateContext(context: ReportTooltipTemplateContext) {
    this.ref?.setInput('context', context);
    this.context = context;
  }

  private updatePosition() {
    if (!this.created) return;

    this.updateOffsets();

    this.container.updatePosition();
  }

  private updateOffsets() {
    this.positionStrategy.withDefaultOffsetX(this.x);
    this.positionStrategy.withDefaultOffsetY(this.y);
  }
}
